import FaviconUrl from '@/assets/favicon.png';
import { Toaster } from '@/components/ui/sonner';
import AnimateFavicon from '@jenmusic/animate-favicon';
import useFlags, { FeatureFlagProvider } from '@jenmusic/feature-flag';
import React, { useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import './App.css';
import { AudioPlayer } from './components/AudioPlayer';
import ErrorBoundary from './components/ErrorBoundary';
import { PageTitle } from './components/PageTitle';
import ScrollToTop from './components/ScrollToTop';
import SideBar from './components/SideBar';
import { FEATURE_FLAG_CONFIG } from './config';
import { useDimensions } from './hooks/useDimensions';
import useInitializeData from './hooks/useInitializeProject';
import Discord from './pages/Discord';
import Discover from './pages/Discover';
import Home from './pages/Home';
import IconGallery from './pages/IconGallery';
import NotFound from './pages/NotFound';
import PrivateShare from './pages/PrivateShare';
import Recharge from './pages/Recharge';
import Share from './pages/Share';
import StyleFilterAdmin from './pages/StyleFilterAdmin';
import StyleFilterDetail from './pages/StyleFilterAdmin/page/StyleFilterDetail';
import StyleFilterMarket from './pages/StyleFilterMarket';
import MarketStyleFilterDetail from './pages/StyleFilterMarket/page/MarketStyleFilterDetail';
import StyleFilterArtistDetail from './pages/StyleFilterMarket/page/StyleFilterArtistDetail';
import SuperTester from './pages/SuperGenerate';
import SuperVoiceTest from './pages/SuperVoiceTest';
import useSidebarStore from './state/sideBarStore';
import { cn } from './utils';
import HomePage from './pages/HomePage';
// import { Button, Icon } from '@jenmusic/jen-ui-library';
import DemoPage from './pages/Demo';
import { useSessionStore } from './hooks/useSessionStore';
import { useAuthCheck } from './hooks/useAuthCheck';
import { useDarkModeStore } from './hooks/useDarkModeStore';
import SuperVoice from './pages/SuperVoice';
import { SuperVoiceCreate } from './pages/SuperVoice/SuperVoiceCreate';
import { SuperVoiceTrackCreate } from './pages/SuperVoice/SuperVoiceTrackCreate';

const App: React.FC = () => {
  const { isDarkMode, isMobileOrPad } = useInitializeData();
  const { session } = useSessionStore();
  const haveToolsPermission = session?.user?.permissions?.some(({ code }) => code === 'SHOW_TOOLS');

  return (
    <div className={cn('w-full h-full', { dark: isDarkMode })}>
      <ErrorBoundary>
        <DndProvider backend={isMobileOrPad ? TouchBackend : HTML5Backend}>
          <FeatureFlagProvider featureFlagConfig={FEATURE_FLAG_CONFIG} userEmail={session.user?.email}>
            <Router>
              <ScrollToTop />
              <PageTitle />
              <main className="w-full h-full">
                <div id="tweakpane-container" className="fixed top-4 right-4 z-50" />
                <Routes>
                  <Route element={<CheckAuthLayout />}>
                    <Route element={<SideBarLayout />}>
                      <Route index path="/" element={<Home />} />
                      <Route path="/folder/:folderId" element={<Home />} />
                      <Route path="/published" element={<Home />} />
                      <Route path="/liked" element={<Home />} />
                      <Route path="/recent" element={<Home />} />
                      <Route path="/uncategorized" element={<Home />} />
                      <Route path="/archived" element={<Home />} />
                      <Route path="/discover" element={<Discover />} />
                      <Route path="/home" element={<HomePage />} />
                      <Route element={<SwitchDarkMode />}>
                        {haveToolsPermission && <Route path="/style-filter-admin" element={<StyleFilterAdmin />} />}
                        {haveToolsPermission && <Route path="/style-filter-admin/:id" element={<StyleFilterDetail />} />}
                      </Route>
                      <Route path="/super_voice" element={<SuperVoice />} />
                      <Route element={<SuperVoiceFeatureCheck />}>
                        <Route path="/super_voice/create" element={<SuperVoiceCreate />} />
                        <Route path="/super_voice/create/:id" element={<SuperVoiceTrackCreate />} />
                        <Route path="/super_voice/track_create" element={<SuperVoiceTrackCreate />} />
                      </Route>
                    </Route>
                    <Route path="/icons" element={<IconGallery />} />
                    <Route path="/recharge" element={<Recharge />} />
                    <Route path="/recharge/:historyId" element={<Recharge />} />
                    <Route path="/private-s/:id" element={<PrivateShare />} />
                    <Route path="/s/:id" element={<Share />} />
                    <Route path="/discord" element={<Discord />} />
                    <Route path="/super_tester" element={<SuperTester />} />
                    <Route path="/super_voice_test" element={<SuperVoiceTest />} />
                    <Route path="/*" element={<NotFound />} />
                    <Route path="/demo" element={<DemoPage />} />
                  </Route>

                  <Route element={<SideBarLayout />}>
                    <Route path="/marketplace" element={<StyleFilterMarket />} />
                    <Route path="/marketplace/artist/:artistName" element={<StyleFilterArtistDetail />} />
                    <Route path="/marketplace/filter/:filterName" element={<MarketStyleFilterDetail />} />
                  </Route>
                </Routes>
              </main>
            </Router>
            <AnimateFavicon sequences={[FaviconUrl]} loop={1} />
            <AudioPlayer />
            <Toaster />
          </FeatureFlagProvider>
        </DndProvider>
      </ErrorBoundary>
    </div>
  );
};

const SwitchDarkMode = () => {
  const { setDarkMode } = useDarkModeStore();
  useEffect(() => {
    setDarkMode(true);
    return () => {
      setDarkMode(false);
    };
  }, []);
  return <Outlet />;
};

const SideBarLayout = () => {
  const { isSidebarOpen } = useSidebarStore();
  const { isMobileOrPad } = useDimensions();
  const { showNewNavigationBar } = useFlags();

  if (showNewNavigationBar) {
    return (
      <div className="flex w-full h-full ">
        {!isMobileOrPad && (
          <div
            className={` transition-all border-r border-jenmusic-background-primary flex-shrink-0 duration-300 ease-in-out ${isSidebarOpen ? 'w-[260px]' : 'w-[80px]'}`}
          >
            <SideBar />
          </div>
        )}
        <div className=" w-full !overflow-y-scroll">
          <Outlet />
        </div>
      </div>
    );
  }
  return <Outlet />;
};
const CheckAuthLayout = () => {
  const { isAuth } = useAuthCheck();
  if (!isAuth) {
    return <div className="w-full h-full bg-black"></div>;
  }
  return <Outlet />;
};

const SuperVoiceFeatureCheck = () => {
  const { showSuperVoice } = useFlags<typeof FEATURE_FLAG_CONFIG>();
  if (showSuperVoice) {
    return <Outlet />;
  }
  return null;
};

export default App;
